/**
 * HeartbeatService
 *
 * Sends a POST to /heartbeat/ every N seconds with a unique tab_id (UUID v4
 * generated once per browser tab).  The backend stores a Redis key per tab with
 * a TTL that auto-expires stale tabs.
 *
 * The polling interval is controlled by the backend via the
 * `heartbeat_interval` field in the response (milliseconds).  If the key is
 * not set in Redis the backend returns the default (20 000 ms).  When the
 * returned interval differs from the current one, the polling loop is
 * automatically rescheduled.
 *
 * The response also carries a `maintenance` flag + message.  When maintenance
 * mode is active the service emits a callback so the UI can block the screen.
 */

import { client, client2 } from '@/api/client'

const DEFAULT_INTERVAL_MS = 20000

/**
 * Generate a UUID-v4 (crypto-safe when available, Math.random fallback).
 */
function uuidv4 () {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const TAB_ID = uuidv4()

let _intervalId = null
let _currentIntervalMs = DEFAULT_INTERVAL_MS
let _onMaintenance = null   // callback(maintenance: bool, message: string)
let _onHeartbeat = null     // callback(data: object)
let _userId = null           // user id passed from the caller
// Set to true the first time the backend redirects the API endpoint to a
// different URL than the one this tab is using (blue/green cutover). The router
// guard reads this to force a full reload so the matching frontend build is
// fetched. Stays true until the page reloads.
let _updateAvailable = false

/**
 * (Re)schedule the polling loop with the given interval.
 */
function _schedule (intervalMs) {
  if (_intervalId) clearInterval(_intervalId)
  _currentIntervalMs = intervalMs
  _intervalId = setInterval(_sendHeartbeat, _currentIntervalMs)
}

async function _sendHeartbeat () {
  try {
    const { data } = await client.post('heartbeat/', { tab_id: TAB_ID, user_id: _userId })

    if (_onHeartbeat) _onHeartbeat(data)

    if (_onMaintenance) {
      _onMaintenance(data.maintenance, data.maintenance_message)
    }

    // Apply API endpoint overrides from the backend (blue/green switching)
    let endpointSwitched = false
    if (data.api_endpoint && client && client.defaults.baseURL !== data.api_endpoint) {
      console.info('[HeartbeatService] Switching API endpoint:', client.defaults.baseURL, '->', data.api_endpoint)
      client.defaults.baseURL = data.api_endpoint
      endpointSwitched = true
    }
    if (data.api_endpoint2 && client2 && client2.defaults.baseURL !== data.api_endpoint2) {
      console.info('[HeartbeatService] Switching API endpoint2:', client2.defaults.baseURL, '->', data.api_endpoint2)
      client2.defaults.baseURL = data.api_endpoint2
      endpointSwitched = true
    }
    // The API endpoint was redirected to a different backend (e.g. a blue/green
    // cutover). Flag it so the router guard can force a full reload on the next
    // navigation, picking up the matching frontend build.
    if (endpointSwitched) _updateAvailable = true

    // Dynamically adjust polling interval if the backend says so
    const serverInterval = data.heartbeat_interval
    if (serverInterval && serverInterval !== _currentIntervalMs) {
      _schedule(serverInterval)
    }
  } catch (err) {
    // Silently ignore network / server errors to avoid false maintenance
    // alarms.  Maintenance mode is only activated when the backend
    // explicitly returns { maintenance: true }.
    console.warn('Heartbeat request failed:', err.message || err)
  }
}

/**
 * Start the heartbeat polling loop.
 *
 * @param {Object}   opts
 * @param {Number}   opts.userId         The current user's id
 * @param {Function} opts.onMaintenance  (isMaintenance: bool, message: string) => void
 * @param {Function} [opts.onHeartbeat]  (data: object) => void  — raw response
 */
function start ({ userId, onMaintenance, onHeartbeat } = {}) {
  if (_intervalId) return // already running

  _userId = userId || null
  _onMaintenance = onMaintenance || null
  _onHeartbeat = onHeartbeat || null

  // Fire immediately, then schedule at the default interval (will auto-adjust)
  _sendHeartbeat()
  _schedule(DEFAULT_INTERVAL_MS)
}

function stop () {
  if (_intervalId) {
    clearInterval(_intervalId)
    _intervalId = null
  }
}

/**
 * Returns true once the backend has redirected this tab's API endpoint to a
 * different URL (blue/green cutover). The router guard uses this to force a
 * full reload so the matching frontend build is fetched.
 */
function isUpdateAvailable () {
  return _updateAvailable
}

export default {
  TAB_ID,
  start,
  stop,
  isUpdateAvailable,
}
